import { Box, SxProps, Theme } from "@mui/material";
import ConcernPill, { willConcernPillRender } from "./ConcernPill";
import {
  TaskBasicData,
  TaskPatientData,
  taskConcernFlags,
} from "../features/data/patientApiSlice";
import { combineSx, createStyleSheet } from "../utils/styles";

import React from "react";

const ConcernPillList = (props: {
  tasks: TaskBasicData[] | TaskPatientData[];
  includeRequestToChat?: boolean;
  overlap?: boolean;
  singlePillHasText?: boolean;
  maxPills?: number;
  sx?: SxProps<Theme>;
}) => {
  const {
    tasks,
    includeRequestToChat,
    overlap,
    singlePillHasText,
    maxPills,
    sx,
  } = props;

  let concernCount = 0;
  tasks.forEach((task) => {
    const flags = taskConcernFlags(task);
    // A task type can render a pill with no concern flag at all (e.g.
    // request_to_chat), so fall back to a single null-flag pill.
    const flagsToCheck = flags.length > 0 ? flags : [null];
    flagsToCheck.forEach((flag) => {
      if (willConcernPillRender(task.type, flag, includeRequestToChat)) {
        concernCount++;
      }
    });
  });

  if (concernCount > 0) {
    const actualOverlap = overlap && (concernCount > 1 || !singlePillHasText);
    const renderedConcerns: React.ReactNode[] = [];
    tasks.forEach((task) => {
      const flags = taskConcernFlags(task);
      const flagsToRender = flags.length > 0 ? flags : [null];
      flagsToRender.forEach((flag) => {
        if (
          (!maxPills || renderedConcerns.length < maxPills) &&
          willConcernPillRender(task.type, flag, includeRequestToChat)
        ) {
          renderedConcerns.push(
            <ConcernPill
              key={`${task.uid}:${flag ?? ""}`}
              taskType={task.type}
              taskState={task.state}
              concernFlagText={flag}
              iconOnly={actualOverlap || !singlePillHasText}
              includeRequestToChat={includeRequestToChat}
              sx={{
                marginLeft: actualOverlap ? -3 : 0,
                marginRight: actualOverlap ? 1 : 0,
                zIndex: 0,
              }}
            />
          );
        }
      });
    });
    return (
      <Box
        sx={combineSx(
          actualOverlap ? styles.containerOverlap : styles.container,
          sx
        )}
      >
        {actualOverlap ? renderedConcerns.reverse() : renderedConcerns}
      </Box>
    );
  } else {
    return null;
  }
};

const styles = createStyleSheet({
  container: {
    display: "flex",
    flexDirection: "row",
    // A task can carry several concerns, and about three text pills fill the
    // row, so let them wrap rather than overflow.  minWidth lets this shrink
    // below its content width when it's a flex item, which is what makes
    // flexWrap take effect.
    flexWrap: "wrap",
    minWidth: 0,
    gap: 0.5,
    marginLeft: 1,
  },
  containerOverlap: {
    display: "flex",
    flexDirection: "row-reverse",
    paddingLeft: 4,
    WebkitMaskImage: "linear-gradient(90deg, #000 60px, transparent);",
  },
});

export default ConcernPillList;
